/**
 * External Review Page - For people who receive a review request email
 * No login required - uses the request token
 */
import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { NavBar } from "@/components/NavBar";
import { Footer } from "@/components/Footer";
import { toast } from "sonner";
import { Star, Loader2, Check, User } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { WORK_TYPES } from "@/lib/profileOptions";

const API = process.env.REACT_APP_BACKEND_URL || "";

export default function ReviewRequestPage() {
  const { requestId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submittedData, setSubmittedData] = useState(null);
  const [request, setRequest] = useState(null);
  const [error, setError] = useState(null);
  
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [comment, setComment] = useState("");
  const [reviewerName, setReviewerName] = useState("");
  const [reviewerEmail, setReviewerEmail] = useState("");
  const [wouldHireAgain, setWouldHireAgain] = useState(null);

  useEffect(() => {
    fetchRequest();
  }, [requestId]);

  const fetchRequest = async () => {
    try {
      const res = await fetch(`${API}/api/reviews/request/${requestId}`);
      if (res.ok) {
        const data = await res.json();
        setRequest(data);
      } else {
        const err = await res.json();
        setError(err.detail || "Review request not found");
      }
    } catch (e) {
      setError("Failed to load review request");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!rating) {
      toast.error("Please select a rating");
      return;
    }
    if (!reviewerName.trim()) {
      toast.error("Please enter your name");
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch(`${API}/api/reviews/request/${requestId}/submit`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          rating,
          comment: comment.trim() || null,
          reviewer_name: reviewerName.trim(),
          reviewer_email: reviewerEmail.trim() || null,
          would_hire_again: wouldHireAgain,
        })
      });

      if (res.ok) {
        const data = await res.json();
        setSubmitted(true);
        setSubmittedData(data);
        toast.success("Review submitted! Thank you.");
      } else {
        const err = await res.json();
        toast.error(err.detail || "Failed to submit review");
      }
    } catch (e) {
      toast.error("Failed to submit review");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] text-white flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-white/50" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] text-white">
        <NavBar />
        <main className="pt-24 px-4">
          <Card className="max-w-lg mx-auto bg-white/[0.03] border-white/10 p-8 text-center">
            <h1 className="text-2xl font-bold mb-4">Review Request</h1>
            <p className="text-white/60">{error}</p>
          </Card>
        </main>
      </div>
    );
  }

  if (submitted) {
    const isNonUser = submittedData && !submittedData.is_existing_user;
    return (
      <div className="min-h-screen bg-[#0a0a0a] text-white flex flex-col">
        <NavBar />
        <main className="flex-1 pt-24 px-4">
          <Card className="max-w-lg mx-auto bg-white/[0.03] border-white/10 p-8 text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-coral/20 flex items-center justify-center">
              <Check className="w-8 h-8 text-coral" />
            </div>
            <h1 className="text-2xl font-bold mb-4" data-testid="review-thank-you">Thank You!</h1>
            <p className="text-white/60 mb-6">
              Your review has been submitted and will help {request?.requester?.name} get discovered by more studios.
            </p>

            {isNonUser && (
              <div className="border border-white/10 rounded-lg p-6 mb-6 bg-white/[0.02]">
                <h3 className="text-lg font-semibold mb-2">Join GottaSecond</h3>
                <p className="text-white/50 text-sm mb-4">
                  Are you a studio owner or photographer? Create a free account to find second shooters, post jobs, and grow your network.
                </p>
                <Link to="/register">
                  <Button className="bg-coral hover:bg-coral/90 text-white font-bold w-full" data-testid="post-review-signup-btn">
                    Create a Free Account
                  </Button>
                </Link>
              </div>
            )}

            <Button onClick={() => navigate("/")} variant="outline" className="border-white/20 text-white" data-testid="visit-gottasecond-btn">
              Visit GottaSecond
            </Button>
          </Card>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white flex flex-col">
      <NavBar />
      
      <main className="flex-1 pt-24 pb-12 px-4">
        <Card className="max-w-lg mx-auto bg-white/[0.03] border-white/10 p-8">
          <h1 className="text-2xl font-bold mb-2 text-center">Leave a Review</h1>
          <p className="text-white/50 text-center mb-6">
            for <span className="text-white font-medium">{request?.requester?.name}</span>
          </p>

          {/* Requester info */}
          <div className="flex items-center gap-4 p-4 bg-white/[0.03] rounded-sm mb-4">
            {request?.requester?.profile_image ? (
              <img 
                src={request.requester.profile_image} 
                alt={request.requester.name}
                className="w-16 h-16 rounded-full object-cover"
              />
            ) : (
              <div className="w-16 h-16 rounded-full bg-white/10 flex items-center justify-center">
                <User className="w-8 h-8 text-white/40" />
              </div>
            )}
            <div>
              <p className="font-medium text-lg">{request?.requester?.name}</p>
              <p className="text-white/40 text-sm">GottaSecond Member</p>
            </div>
          </div>

          {/* Roles performed */}
          {request?.roles_performed && request.roles_performed.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mb-6">
              {request.roles_performed.map(function(role) {
                var wt = WORK_TYPES.find(function(w) { return w.value === role; });
                return (
                  <Badge key={role} className="bg-white/[0.06] text-white/60 border-white/10 text-xs">
                    {wt ? wt.label : role}
                  </Badge>
                );
              })}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Star Rating */}
            <div>
              <Label className="text-sm text-white/60 mb-2 block">Rating *</Label>
              <div className="flex gap-1">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    type="button"
                    onClick={() => setRating(star)}
                    onMouseEnter={() => setHoverRating(star)}
                    onMouseLeave={() => setHoverRating(0)}
                    className="p-1 transition-transform hover:scale-110"
                  >
                    <Star
                      className={`w-8 h-8 ${
                        star <= (hoverRating || rating)
                          ? "fill-coral text-coral"
                          : "text-white/20"
                      }`}
                    />
                  </button>
                ))}
              </div>
            </div>

            {/* Comment */}
            <div>
              <Label className="text-sm text-white/60 mb-2 block">Your Review (optional)</Label>
              <Textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="Share your experience working with this person..."
                className="bg-white/[0.05] border-white/10 text-white min-h-[100px]"
                data-testid="review-comment"
              />
            </div>

            {/* Would Hire Again */}
            <div>
              <Label className="text-sm text-white/60 mb-2 block">Would you hire them again?</Label>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setWouldHireAgain(wouldHireAgain === true ? null : true)}
                  className={`flex-1 py-3 rounded-sm border text-sm font-medium transition-all ${
                    wouldHireAgain === true
                      ? "border-green-500 bg-green-500/15 text-green-400"
                      : "border-white/10 bg-white/[0.03] text-white/60 hover:border-white/20"
                  }`}
                  data-testid="would-hire-yes"
                >
                  Yes
                </button>
                <button
                  type="button"
                  onClick={() => setWouldHireAgain(wouldHireAgain === false ? null : false)}
                  className={`flex-1 py-3 rounded-sm border text-sm font-medium transition-all ${
                    wouldHireAgain === false
                      ? "border-red-500 bg-red-500/15 text-red-400"
                      : "border-white/10 bg-white/[0.03] text-white/60 hover:border-white/20"
                  }`}
                  data-testid="would-hire-no"
                >
                  No
                </button>
              </div>
            </div>

            {/* Reviewer Name */}
            <div>
              <Label className="text-sm text-white/60 mb-2 block">Your Name *</Label>
              <Input
                value={reviewerName}
                onChange={(e) => setReviewerName(e.target.value)}
                placeholder="How should we display your name?"
                className="bg-white/[0.05] border-white/10 text-white"
                required
              />
            </div>

            {/* Reviewer Email (optional) */}
            <div>
              <Label className="text-sm text-white/60 mb-2 block">Your Email (optional)</Label>
              <Input
                type="email"
                value={reviewerEmail}
                onChange={(e) => setReviewerEmail(e.target.value)}
                placeholder="For verification purposes only"
                className="bg-white/[0.05] border-white/10 text-white"
              />
              <p className="text-white/30 text-xs mt-1">Your email will not be displayed publicly</p>
            </div>

            <Button
              type="submit"
              disabled={submitting}
              className="w-full bg-coral hover:bg-coral/90 text-white font-bold py-3"
            >
              {submitting ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                "Submit Review"
              )}
            </Button>
          </form>
        </Card>
      </main>
      <Footer />
    </div>
  );
}
